import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import DeleteIcon from "@mui/icons-material/DeleteForeverRounded";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RAIL_LABEL } from "../../../constants/configuration.const";
import DragHandleIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import {
    Autocomplete,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    Paper,
    TextField,
    Tooltip,
    Typography,
    createFilterOptions,
    styled,
    RadioGroup,
    Radio,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import {
    BANNER_ACCEPTED_FILE,
    BANNER_DIMENSION,
    BANNER_ERROR_MESSAGE,
    BANNER_PLACEHOLDER_MESSAGE_WEB,
    GENRE_RAIL_TITLE,
    SINGULAR_MOBILE_MIN_IMAGE_DIMENSION,
    SINGULAR_PROMO_MOBILE_PLACEHOLDER_MESSAGE,
    SINGULAR_PROMO_VALIDATION,
    SINGULAR_PROMO_WEB_PLACEHOLDER_MESSAGE,
    SINGULAR_WEB_MIN_IMAGE_DIMENSION,
    SYNOPSIS_LIMIT,
    THEMATIC_MIN_IMAGE_DIMENSION,
    THEMATIC_PLACEHOLDER_MESSAGE,
    THEMATIC_VALIDATION,
} from "../LandingPage/constants";
import { StyledDropzone } from "../../UI/Common/FileUploader";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";

import GeneralModalV1 from "../../UI/atoms/Popup/GeneralModalV1";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const SingularPromoContentCurationCard = forwardRef(
    (
        {
            handleFormChange,
            videos,
            dragHandleProps,
            onClickDelete,
            onClickEdit,
            onClickAdd,
            data,
            className,
            hideAddButton,
            allowEdit,
            handleRail,
            handleRailTitle,
        }: any,
        ref: any
    ) => {
        const initialFormValue = {
            title: "",
            synopsis: "",
            contentId: "",
            backgroundColor: "",
            imageUrl: "",
            watchNow: true,
            watchList: true,
            download: true,
        };

        // hooks
        const [showForm, setShowForm] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const singularPromoRef = useRef<any>(null);
        const [errorMessage, setErrorMessage] = useState("");
        const [enableTitleEdit, setEnableTitleEdit] = useState(false);
        const [imageErrMessage, setImageErrMessage] = useState("");
        const [mobileImageErrMessage, setMobileImageErrMessage] = useState("");
        const [webImageLoader, setWebImageLoader] = useState(false);
        const [mobileImageLoader, setMobileImageLoader] = useState(false);
        const [initialFormValueState, setInitialFormValueState] =
            useState<any>(initialFormValue);
        const [videoListInfo, setVideoListInfo] = useState<any>([]);
        const [isOpen, setIsOpen] = React.useState(false);
        const [showInfoPopup, setShowInfoPopup] = useState(false);
        const [selectedFile, setSelectedFile] = useState<any>(null);
        const [singularPromoTitle, setSingularPromoTitle] = useState("");
        const [formValid, setFormValid] = useState<any>(null);
        const [formChangeKey, setFormChange] = useState<any>(null);

        useImperativeHandle(
            ref,
            () => ({
                onOpenForm,
                isValid,
                handleSubmit,
                getFormValue,
            }),
            [showForm, formChangeKey, formValid]
        );

        // manual formchange event
        const onFormChange = (field: string, value: string) => {
            setFormChange({ field, value });
        };

        // for the parent to access tje form isValid state
        const isValid = () => {
            // if form is already opened, get data from the formRef
            // else get it from the state
            if (showForm) {
                return singularPromoRef.current.isValid;
            } else {
                return formValid?.isValid == null ? null : formValid?.isValid;
            }
        };

        // for the parent to access the form submit
        const handleSubmit = () => {
            setShowForm(true);
            setTimeout(() => {
                singularPromoRef.current.handleSubmit();
            }, 100);
        };
        const getFormValue = () =>
            singularPromoRef?.current?.values || formValid.values;

        useEffect(() => {
            checkIfKeyPresent(data);
            setInitialFormValueState({
                synopsis: data?.synopsis || "",
                imageUrl: data?.imageUrl || "",
                mobileImageUrl: data?.mobileImageUrl || "",
                contentId: data?.contentId || "",
                title: data?.title || "",
                watchNow: true,
                watchList: true,
                download: true,
                align: data?.align || "left",
            });
        }, [data]);

        // on first time creation, there will not be a key called "synopsis". this is make the form valid to false
        const checkIfKeyPresent = (data: any) => {
            if (!data) return;
            const keys = Object.keys(data);
            if (!keys.includes("synopsis")) {
                setFormValid({ isValid: false });
            }
        };

        const handleOpenOptions = () => {
            setIsOpen(true);
        };

        const handleCloseOptions = () => {
            setIsOpen(false);
        };

        useEffect(() => {
            if (videos && videos.length) setVideoListInfo(videos);
        }, [videos]);

        useEffect(() => {
            if (data) {
                setSingularPromoTitle(data?.railTitle);
            }
        }, [data]);

        useEffect(() => {
            if (singularPromoRef?.current && singularPromoTitle) {
                singularPromoRef.current.setFieldValue(
                    "railTitle",
                    singularPromoTitle || ""
                );
            }
        }, [singularPromoTitle && singularPromoRef]);

        const onOpenForm = () => {
            setShowForm(true);
            setTimeout(() => {
                singularPromoRef.current.validateForm();
            }, 100);
        };

        // when closing the rail, make sure to persist the form validate & the form values
        const onCloseRail = () => {
            const { isValid, values } = singularPromoRef.current;
            setFormValid({ isValid, values });
            setShowForm(false);
            handleRail(singularPromoRef.current.values);
        };

        // on click on save button inside the rail
        const saveSingularPromoRail = () => {
            // moves data to the parent
            const { isValid, values } = singularPromoRef.current;
            setFormValid({ isValid, values });
            handleRail(singularPromoRef.current.values);
            enqueueSnackbar("Singular Promo Rail Added", {
                variant: "info",
                autoHideDuration: 2000,
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                },
            });
            setErrorMessage("");
            setShowForm(false);
        };

        // upload image
        const uploadImage = (files: any, fieldName: string) => {
            // check if the image is already -present
            const singulaPromoRail = singularPromoRef.current.values;
            if (singulaPromoRail && singulaPromoRail[fieldName]) {
                // open dialog for confirmation
                setShowInfoPopup(true);
                setSelectedFileValue(files, fieldName);
            } else {
                validateAndUploadFile(files[0], fieldName);
            }
        };

        const setSelectedFileValue = (files: any, fieldName: any = null) => {
            if (files) setSelectedFile({ files, fieldName });
            else setSelectedFile(null);
        };

        // Validate the file dimension & proceed with uploading the file
        const validateAndUploadFile = (file: any, fieldName: string) => {
            let minHeight: any, minWidth: any, setImgErrMessage: any;
            if (fieldName == "mobileImageUrl") {
                minHeight = SINGULAR_MOBILE_MIN_IMAGE_DIMENSION.HEIGHT;
                minWidth = SINGULAR_MOBILE_MIN_IMAGE_DIMENSION.WIDTH;
                setImgErrMessage = setMobileImageErrMessage;
            } else {
                minHeight = SINGULAR_WEB_MIN_IMAGE_DIMENSION.HEIGHT;
                minWidth = SINGULAR_WEB_MIN_IMAGE_DIMENSION.WIDTH;
                setImgErrMessage = setImageErrMessage;
            }
            // CHECK ACCEPTED FILE FORMATS
            if (!(file && BANNER_ACCEPTED_FILE.includes(file.type))) return;
            const image = new Image();
            image.addEventListener("load", () => {
                // check if the width>= 3840 & height>=2160
                if (image.width >= minWidth && image.height >= minHeight) {
                    //incase if the image container already has error message shown, remove it since dimension is good for upload
                    uploadImageAPI(file, fieldName);
                    setImgErrMessage("");
                } else {
                    // if the dimension is not good enough. Add the index of the banner to the error
                    if (!imageErrMessage) {
                        setImgErrMessage(BANNER_ERROR_MESSAGE);
                    }
                }
            });
            image.src = URL.createObjectURL(file);
        };

        // API for uploading image
        const uploadImageAPI = async (file: any, fieldName: string) => {
            let setLoader: any =
                fieldName == "mobileImageUrl"
                    ? setMobileImageLoader
                    : setWebImageLoader;
            try {
                if (file) {
                    const formData = new FormData();
                    formData.append("file", file);
                    setLoader(true);
                    const uploadFile: any = await axios.post(
                        `${process.env.BASE_URL}api/v1/cms/page/upload-banner`,
                        formData
                    );
                    if (uploadFile?.data?.success) {
                        // if upload is success, update the image url of the specified banner index
                        const imageUrl = uploadFile?.data?.data?.url;
                        let singulaPromoRail = singularPromoRef.current.values;
                        singulaPromoRail[fieldName] = imageUrl;
                        singularPromoRef.current.setValues({
                            ...singulaPromoRail,
                        });
                        enqueueSnackbar("Image Uploaded", {
                            variant: "info",
                            autoHideDuration: 2000,
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "center",
                            },
                        });
                    }
                }
            } catch (error: any) {
                if (error?.response?.data?.message) {
                    enqueueSnackbar(error?.response?.data?.message, {
                        variant: "error",
                    });
                } else {
                    enqueueSnackbar("Error when uploading image", {
                        variant: "error",
                    });
                }
            } finally {
                setLoader(false);
            }
        };

        const filterOptions = createFilterOptions({
            matchFrom: "any",
            stringify: (option: any) => option.name + option.id,
        });

        const renderOptionsInitial = (options: any) => {
            if (typeof options == "string") {
                const filterVideo = videoListInfo.filter(
                    (list: any) => list.id === options
                );
                if (filterVideo.length > 0) {
                    return filterVideo[0].name + " - " + filterVideo[0].id;
                } else {
                    return "";
                }
            } else if (typeof options == "object") {
                return options.name + " - " + options.id;
            }
        };

        const closeInfoModal = () => {
            setShowInfoPopup(false);
            setSelectedFileValue(null);
        };

        const confirmUpload = () => {
            validateAndUploadFile(
                selectedFile.files[0],
                selectedFile.fieldName
            );
            setShowInfoPopup(false);
        };

        return (
            <div
                className={
                    "curation-list-card-container d-flex j-center curation-card"
                }
            >
                <div {...dragHandleProps} className="d-flex float-center">
                    <DragHandleIcon className="drag-handle" />
                </div>
                <div className="curation-list-card hero-carousal-card-height">
                    {/* Row One */}
                    <div {...dragHandleProps} className="d-flex j-space">
                        <span className="curation-title">
                            #{RAIL_LABEL[data.template]}
                        </span>
                        <div className="f-bold">
                            {enableTitleEdit ? (
                                <>
                                    <TextField
                                        label="Singular Promo Title"
                                        id="outlined-size-small"
                                        value={singularPromoTitle}
                                        size="small"
                                        onChange={(e: any) => {
                                            const title = e.target.value || "";
                                            setSingularPromoTitle(title);
                                        }}
                                    />
                                    <Tooltip title="Save Title">
                                        <IconButton
                                            disabled={
                                                singularPromoTitle.length == 0
                                            }
                                        >
                                            <Check
                                                onClick={() => {
                                                    setEnableTitleEdit(false);
                                                    handleRailTitle(
                                                        singularPromoTitle
                                                    );
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                        <IconButton>
                                            <Close
                                                onClick={() => {
                                                    setSingularPromoTitle(
                                                        data?.railTitle
                                                    );
                                                    setEnableTitleEdit(false);
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <span
                                    onClick={() =>
                                        allowEdit &&
                                        showForm &&
                                        setEnableTitleEdit(true)
                                    }
                                >
                                    {" "}
                                    {singularPromoTitle}
                                </span>
                            )}
                            <div></div>
                        </div>
                        <div>
                            <span
                                className="cursor-pointer delete-circle-style"
                                onClick={onClickDelete}
                            >
                                {allowEdit && <DeleteIcon />}
                            </span>
                        </div>
                    </div>

                    {/* row content */}

                    {showForm && (
                        <Formik
                            innerRef={singularPromoRef}
                            initialValues={initialFormValueState}
                            enableReinitialize
                            validationSchema={Yup.object({
                                title: Yup.string().required(
                                    "Title Text is required"
                                ),
                                synopsis: Yup.string()
                                    .required("Synopsis is required")
                                    .max(
                                        SINGULAR_PROMO_VALIDATION.DESCRIPTION,
                                        `Synopsis must not exceed ${SINGULAR_PROMO_VALIDATION.DESCRIPTION} characters`
                                    ),
                                contentId: Yup.string().required(
                                    "Content ID is required"
                                ),
                                imageUrl:
                                    Yup.string().required("Image is required"),
                                mobileImageUrl: Yup.string().required(
                                    "Mobile Image is required"
                                ),
                                align: Yup.string().required(
                                    "Hero Image  alignment is required"
                                ),
                            })}
                            onSubmit={saveSingularPromoRail}
                        >
                            {({
                                values,
                                handleChange,
                                touched,
                                errors,
                                setFieldValue,
                                setTouched,
                            }: any) => (
                                <Form>
                                    <Grid className="p-1" container spacing={2}>
                                        {/* First Column */}
                                        <Grid
                                            item
                                            xs={12}
                                            md={4}
                                            className="mb-2"
                                        >
                                            <div>
                                                <Typography
                                                    fontStyle={"bold"}
                                                    variant="subtitle2"
                                                >
                                                    CTV/Web/Tablet Image
                                                </Typography>
                                                <Item>
                                                    <StyledDropzone
                                                        height={100}
                                                        loaderHeight={100}
                                                        disabled={!allowEdit}
                                                        onDropFile={(
                                                            file: any
                                                        ) => {
                                                            onFormChange(
                                                                "imageUrl",
                                                                values.imageUrl
                                                            );
                                                            uploadImage(
                                                                file,
                                                                "imageUrl"
                                                            );
                                                            setTouched({
                                                                imageUrl: true,
                                                            });
                                                        }}
                                                        multiple={false}
                                                        onRemoveImage={() => {
                                                            onFormChange(
                                                                "imageUrl",
                                                                ""
                                                            );
                                                            setFieldValue(
                                                                `imageUrl`,
                                                                null
                                                            );
                                                            setTouched({
                                                                imageUrl: true,
                                                            });
                                                        }}
                                                        imageUrl={
                                                            values[`imageUrl`]
                                                        }
                                                        errorMessage={
                                                            imageErrMessage
                                                        }
                                                        errorOutline={
                                                            touched?.imageUrl &&
                                                            errors.imageUrl
                                                        }
                                                        loader={webImageLoader}
                                                        placeholderMessage={""}
                                                    />
                                                </Item>
                                            </div>
                                            <div className="mt-1">
                                                <Typography
                                                    fontStyle={"bold"}
                                                    variant="subtitle2"
                                                >
                                                    Mobile Image
                                                </Typography>
                                                <Item>
                                                    <StyledDropzone
                                                        height={100}
                                                        loaderHeight={100}
                                                        disabled={!allowEdit}
                                                        onDropFile={(
                                                            file: any
                                                        ) => {
                                                            onFormChange(
                                                                "mobileImageUrl",
                                                                values.mobileImageUrl
                                                            );
                                                            uploadImage(
                                                                file,
                                                                "mobileImageUrl"
                                                            );
                                                            setTouched({
                                                                mobileImageUrl:
                                                                    true,
                                                            });
                                                        }}
                                                        multiple={false}
                                                        onRemoveImage={() => {
                                                            onFormChange(
                                                                "mobileImageUrl",
                                                                ""
                                                            );
                                                            setFieldValue(
                                                                `mobileImageUrl`,
                                                                null
                                                            );
                                                            setTouched({
                                                                mobileImageUrl:
                                                                    true,
                                                            });
                                                        }}
                                                        imageUrl={
                                                            values[
                                                                `mobileImageUrl`
                                                            ]
                                                        }
                                                        errorMessage={
                                                            mobileImageErrMessage
                                                        }
                                                        errorOutline={
                                                            touched?.mobileImageUrl &&
                                                            errors.mobileImageUrl
                                                        }
                                                        loader={
                                                            mobileImageLoader
                                                        }
                                                        placeholderMessage={""}
                                                    />
                                                </Item>
                                            </div>
                                        </Grid>

                                        {/* Second Column */}
                                        <Grid item xs={12} md={4}>
                                            {/* <InputLabel shrink>Playlist Id</InputLabel> */}

                                            <Field
                                                name={`title`}
                                                as={TextField}
                                                label="Title Text"
                                                fullWidth
                                                value={values[`title`]}
                                                onChange={(e: any) => {
                                                    handleChange(e);
                                                    onFormChange(
                                                        "title",
                                                        e.target.value
                                                    );
                                                }}
                                                size={"small"}
                                                className="mb-1 w-95"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={
                                                    touched?.title &&
                                                    errors.title
                                                }
                                                helperText={
                                                    touched.title &&
                                                    errors.title
                                                }
                                                disabled={!allowEdit}
                                            />
                                            <div className="mt-2">
                                                <Typography variant="body1">
                                                    Buttons
                                                </Typography>
                                                <Typography variant="caption">
                                                    All form factors
                                                </Typography>
                                                <div className="d-flex flex-column">
                                                    <FormControlLabel
                                                        control={<Checkbox />}
                                                        label={"Watch Now"}
                                                        checked={
                                                            values.watchNow
                                                        }
                                                    />
                                                    <FormControlLabel
                                                        control={<Checkbox />}
                                                        label={"Watchlist"}
                                                        checked={
                                                            values.watchList
                                                        }
                                                    />
                                                </div>

                                                <Typography variant="caption">
                                                    Mobile Only
                                                </Typography>
                                                <div>
                                                    <FormControlLabel
                                                        control={<Checkbox />}
                                                        label={"Download"}
                                                        checked={
                                                            values.download
                                                        }
                                                    />
                                                </div>
                                                <Typography variant="caption">
                                                    Hero Image(CTV/Web/Tablet)
                                                </Typography>
                                                <div>
                                                    <RadioGroup
                                                        name="align"
                                                        value={
                                                            values.align
                                                                ? values.align
                                                                : "left"
                                                        }
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "align",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <FormControlLabel
                                                            value="left"
                                                            control={<Radio />}
                                                            label="Left Aligned"
                                                        />
                                                        <FormControlLabel
                                                            value="right"
                                                            control={<Radio />}
                                                            label="Right Aligned"
                                                        />
                                                    </RadioGroup>
                                                </div>
                                            </div>
                                        </Grid>

                                        {/* Third Column */}
                                        <Grid item xs={12} md={4}>
                                            <div>
                                                <Autocomplete
                                                    id="videoId"
                                                    options={videoListInfo}
                                                    filterOptions={
                                                        filterOptions
                                                    }
                                                    value={values.contentId}
                                                    includeInputInList
                                                    size="small"
                                                    open={isOpen}
                                                    onChange={(e, value) => {
                                                        onFormChange(
                                                            "contentId",
                                                            value?.id
                                                        );
                                                        setFieldValue(
                                                            "contentId",
                                                            value?.id || ""
                                                        );
                                                        setFieldValue(
                                                            `duration`,
                                                            value?.duration ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `genre`,
                                                            value?.genre || null
                                                        );
                                                        setFieldValue(
                                                            `rating`,
                                                            value?.rating ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `seasonCount`,
                                                            value?.seasonCount ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `initialReleaseYear`,
                                                            value?.initialReleaseYear ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `latestReleaseYear`,
                                                            value?.latestReleaseYear ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `logoUrl`,
                                                            value?.logoUrl ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `playbackStartDate`,
                                                            value?.playbackStartDate ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `playbackEndDate`,
                                                            value?.playbackEndDate ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `comingSoonDate`,
                                                            value?.comingSoonDate ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `newSeasonDate`,
                                                            value?.newSeasonDate ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `movieRef`,
                                                            value?.movieRef ||
                                                                null
                                                        );
                                                        setFieldValue(
                                                            `seriesRef`,
                                                            value?.seriesRef ||
                                                                null
                                                        );
                                                    }}
                                                    onOpen={handleOpenOptions}
                                                    onClose={handleCloseOptions}
                                                    getOptionLabel={(
                                                        options: any
                                                    ) => {
                                                        return (
                                                            renderOptionsInitial(
                                                                options
                                                            ) || ""
                                                        );
                                                    }}
                                                    disabled={!allowEdit}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            name="contentId"
                                                            label="Content Id"
                                                            value={
                                                                values.contentId
                                                            }
                                                            variant="outlined"
                                                            helperText={
                                                                touched.contentId &&
                                                                errors.contentId
                                                            }
                                                            error={
                                                                touched.contentId &&
                                                                errors.contentId
                                                            }
                                                            {...params}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <Field
                                                name={`synopsis`}
                                                as={TextField}
                                                label="Synopsis"
                                                value={values[`synopsis`]}
                                                onChange={(e: any) => {
                                                    const inputValue =
                                                        e.target.value;
                                                    setFieldValue(
                                                        `synopsis`,
                                                        inputValue
                                                    );
                                                    onFormChange(
                                                        "synopsis",
                                                        inputValue
                                                    );
                                                }}
                                                fullWidth
                                                className="mt-1"
                                                size={"small"}
                                                rows={6}
                                                multiline
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                error={
                                                    touched?.synopsis &&
                                                    errors?.synopsis
                                                }
                                                helperText={
                                                    touched.synopsis &&
                                                    errors.synopsis
                                                }
                                                disabled={!allowEdit}
                                            />
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    )}

                    <div
                        {...dragHandleProps}
                        className={`${!showForm && "mt-3 "}d-flex j-space`}
                    >
                        {!showForm && (
                            <>
                                <span
                                    onClick={onOpenForm}
                                    className="curation-edit cursor-pointer"
                                >
                                    Edit Rail
                                </span>
                                <KeyboardArrowDownIcon
                                    className="cursor-pointer"
                                    onClick={onOpenForm}
                                />
                            </>
                        )}
                    </div>

                    {showForm && (
                        <div
                            {...dragHandleProps}
                            className="d-flex flex-column"
                        >
                            <div className="d-flex j-end">
                                {allowEdit && (
                                    <Button
                                        onClick={() =>
                                            singularPromoRef.current.handleSubmit()
                                        }
                                        className="max-content-width float-center align-right mr-2"
                                        variant="contained"
                                        type="button"
                                    >
                                        Save
                                    </Button>
                                )}
                                <KeyboardArrowUpIcon
                                    className="align-right cursor-pointer"
                                    onClick={onCloseRail}
                                />
                            </div>
                            {errorMessage && (
                                <div className="text-danger d-flex j-end publish-error-text">
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div
                    {...dragHandleProps}
                    className="curation-list-card-container-span"
                >
                    {!hideAddButton && allowEdit && (
                        <Tooltip title="Add New Rail">
                            <span
                                className="cursor-pointer add-circle-style"
                                onClick={onClickAdd}
                            >
                                <AddCircleOutlineIcon />
                            </span>
                        </Tooltip>
                    )}
                </div>
                <GeneralModalV1
                    title="Confirm upload"
                    isOpen={showInfoPopup}
                    handleClose={closeInfoModal}
                    handleConfirm={confirmUpload}
                >
                    <p>
                        {" "}
                        Replace current image with{" "}
                        {selectedFile && selectedFile?.files[0]?.name}?
                    </p>
                </GeneralModalV1>
            </div>
        );
    }
);

export default SingularPromoContentCurationCard;
